import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Copy, Pencil, Trash2Icon } from "lucide-react";
import Input from "../../Tags/Input";
import TooltipWrapper from "../../TooltipWrapper";
import Image from "../../Tags/Image";
import { CaretDownIcon } from "@radix-ui/react-icons";
import { useStoreCRMSearch } from "../../../stores/useStoreCRMSearch";
import useModal from "../../GenericModal/useModal";
import GenericModal from "../../GenericModal";
import CompanyRecords from "./CompanyRecords";
import { areAllKeysEmpty, isUUIDKey } from "../../../utils/GenericFn";
import { useStoreCRMConfig } from "../../../stores/useStoreCRMConfig";
import { useStoreCRMCreate } from "../../../stores/useStoreCRMCreate";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Badge } from "../../ui/badge";
import EllipsisText from "../../EllipsisText";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

export default function PeopleOfCompany({
  platformFieldsPerson,
  org,
  query,
  getOrgPayload,
  getPeoplePayload,
  removePersonFromPayload,
  activeCRMIntegration,
  hasReferences,
  disableDelete = false,
  allRecordsAlreadyPushed,
}) {
  const {
    addChunkInCRMBulkSearchData,
    crmBulkSearchData,
    setCRMBulkSearchPayload,
    isCRMBulkSearchLoading,
    isCRMBulkSearchFetched,
    getCRMSearchRecordById,
    getCRMSearchRecordByIdFlat,
  } = useStoreCRMSearch();

  const { crmBulkCreateData, getCRMCreateRecordById } = useStoreCRMCreate();

  const { savedCompanyConfigWithWritePermission, savedPeopleConfigWithWritePermission } = useStoreCRMConfig();

  const captureEvent = useCaptureEvent();

  function generateKeyLabelArray(inputObj) {
    if (!inputObj) {
      return;
    }
    return Object.keys(inputObj).map((outerKey) => {
      const fieldData = inputObj[outerKey];

      return {
        key: outerKey,
        label: fieldData?.label,
        required: fieldData?.required,
        is_custom: fieldData?.is_custom,
      };
    });
  }

  const getPrintableTableData = (systemFields, records) => {
    // const headers = systemFields.map((field) => ({
    //   key: field.key,
    //   label: field.label,
    // }));

    const headers = generateKeyLabelArray(records?.[0]?.data);

    const rows = records.map((record) => {
      const row = {};
      const fieldGroup = record.data;
      // 🧠 Go through each header key to extract data from new nested structure
      headers.forEach(({ key, label }) => {
        let found = false;
        if (fieldGroup && fieldGroup[key]) {
          row[key] = fieldGroup[key]?.value;
          found = true;
        }
        if (!found) {
          row[key] = ""; // fallback if key not found
        }

        if (isUUIDKey(key)) {
          setCustomFieldsObj((prev) => ({
            ...prev,
            [key]: records?.[0]?.data?.[key],
          }));
        }
      });

      row.record_id = record?.person_id || record?.record_id || "";

      const contacts = query.contacts || "[]";

      const foundContact = contacts.find(
        (el) => el.person_id === row.record_id || el.contact_id === row.record_id
      );
      row.isRevealed = foundContact?.isRevealed;

      const crmRecords = getCRMSearchRecordByIdFlat(record?.contact_id || record?.person_id);

      const crmRecordsWithError = getCRMCreateRecordById(record?.contact_id || record?.person_id);

      console.log("crmRecordsWithError", { crmRecordsWithError, crmRecords });
      if (
        crmRecordsWithError &&
        crmRecordsWithError.crm_response_info &&
        crmRecordsWithError.crm_response_info.status !== 200
      ) {
        row.has_error = true;
        row.crm_response_info = crmRecordsWithError.crm_response_info;
      } else {
        row.has_error = false;
      }

      if (crmRecordsWithError && crmRecordsWithError.record_url) {
        row.record_url = crmRecordsWithError.record_url;
        row.record_exists = true;
      } else if (crmRecords && crmRecords.length > 0) {
        row.record_exists = true;
        row.record_url = crmRecords[0]?.record_url;
      } else {
        row.record_exists = false;
      }

      return row;
    });

    return { headers, rows };
  };

  const getInitials = (name = "") => {
    if (!name) return "";

    const words = name.trim().split(/\s+/); // split by spaces
    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }

    return (words[0][0] + words[1][0]).toUpperCase();
  };
  const [customFieldsObj, setCustomFieldsObj] = useState({});
  const [tableHeads, setTableHeads] = useState([]);

  const [tableRowsWithWithoutIsRevealed, setTableRowsWithWithoutIsRevealed] = useState([]);

  // const [editedData, setEditedData] = useState({});
  const [editingCell, setEditingCell] = useState({ rowIndex: null, key: null });
  const handleCellEdit = (rowIndex, key, value) => {
    setTableRowsWithWithoutIsRevealed((prevRows) =>
      prevRows.map((row, idx) =>
        idx === rowIndex
          ? {
              ...row,
              input_fields: {
                ...(row.input_fields || {}),
                [key]: value,
              },
              [key]: value, // Optional: keep in sync for live display
            }
          : row
      )
    );
  };
  const copyCellValueToRows = (sourceRowIndex, key, copyTo = "empty") => {
    setTableRowsWithWithoutIsRevealed((prevRows) => {
      const sourceValue = prevRows[sourceRowIndex]?.[key] ?? prevRows[sourceRowIndex]?.input_fields?.[key];

      return prevRows.map((row, idx) => {
        if (idx === sourceRowIndex) return row; // skip source row

        const existingValue = row[key] ?? row?.input_fields?.[key];

        const shouldCopy = copyTo === "all" || (copyTo === "empty" && !existingValue);

        if (!shouldCopy) return row;

        return {
          ...row,
          input_fields: {
            ...(row.input_fields || {}),
            [key]: sourceValue,
          },
          [key]: sourceValue, // optional, for live display
        };
      });
    });
  };
  const mergeWithExistingInputFields = (existingRows, newRows) => {
    return newRows.map((newRow) => {
      const match = existingRows.find((r) => r.record_id === newRow.record_id);

      if (!match || !match.input_fields) return newRow;

      const preservedFields = match.input_fields;

      // Only override fields if they exist in input_fields
      const updatedRow = { ...newRow };
      Object.keys(preservedFields).forEach((key) => {
        if (key in updatedRow) {
          updatedRow[key] = preservedFields[key];
        }
      });

      return {
        ...updatedRow,
        input_fields: preservedFields,
      };
    });
  };

  useEffect(() => {
    const { headers, rows } = getPrintableTableData(platformFieldsPerson, org.people);

    setTableHeads(headers);

    console.log("setTableRowsWithWithoutIsRevealed", rows);

    setTableRowsWithWithoutIsRevealed((prev) => mergeWithExistingInputFields(prev, rows));
  }, [platformFieldsPerson, org?.people, crmBulkSearchData, query]);

  useEffect(() => {
    console.log("tableRowsWithWithoutIsRevealed", tableRowsWithWithoutIsRevealed);

    if (tableRowsWithWithoutIsRevealed && tableRowsWithWithoutIsRevealed.length > 0) {
      const transformedPayload = tableRowsWithWithoutIsRevealed
        // .filter((person) => !person.record_exists)
        .map((person) => ({
          record_exists: person.record_exists,
          person_id: person.record_id,
          ...(person?.input_fields && { input_fields: person?.input_fields }), // !areAllKeysEmpty(person?.input_fields) &&
        }));
      console.log("transformedPayload people", transformedPayload);
      getPeoplePayload(transformedPayload);
    }
  }, [tableRowsWithWithoutIsRevealed]);

  const [matchedObj, setMatchedObj] = useState({});
  const [selectedRecord, setSelectedRecord] = useState({});
  const [allRecordsWithApiName, setAllRecordsWithApiName] = useState([]);
  const [allRecordsWithApiNameError, setAllRecordsWithApiNameError] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [searchFilterData, setSearchFilterData] = useState({
    name: {
      label: "Account Name",
      value: org?.name,
    },
    social_media_urls_linkedin_url: {
      label: "Company Linkedin URL",
      value: org?.linkedin_url,
    },
    domain: {
      label: "Domain",
      value: org?.domain ? org?.domain : "",
    },
  });

  const processOrgSearchData = (data, shouldSetCompanyId) => {
    if (data && data.length > 0) {
      console.log(data);

      const result = data.flatMap((item) =>
        Array.isArray(item.records) && item.records.length > 0
          ? item.records.map((record) => ({
              ...record,
              apiName: item.crm_object_api_name,
              apiNameLabel: item?.crm_object_label,
              search_filters_applied: item?.search_filters_applied,
            }))
          : []
      );

      console.log("result", result);

      const matchedObj =
        result && result.length > 0
          ? result
              .filter((obj) => obj.match_confirmed)
              .sort((a, b) => new Date(b.record_created_at) - new Date(a.record_created_at))[0]
          : undefined;

      console.log("matchedObj", matchedObj);
      setMatchedObj(matchedObj);
      setSelectedCompanyId(matchedObj?.Id);
      setSelectedRecord(matchedObj);
      if (result && result.length === 1) {
        setSelectedRecord(result[0]);
        setSelectedCompanyId(result[0]?.Id);
      }

      const resultWithError = data.filter((item) => item.error);

      setAllRecordsWithApiNameError(resultWithError);
      setAllRecordsWithApiName(result);
      if (result && result.length > 0 && shouldSetCompanyId) {
        const foundObj = result.find((el) => el?.Id === shouldSetCompanyId);
        setSelectedCompanyId(shouldSetCompanyId);
        setSelectedRecord(foundObj);
      }

      if (data && data.length > 0 && data[0].search_filters_applied) {
        setSearchFilterData(data[0].search_filters_applied);
      }

      if (
        savedCompanyConfigWithWritePermission &&
        savedCompanyConfigWithWritePermission.length > 0 &&
        hasReferences &&
        result &&
        result.length === 0
      ) {
        setShouldCreateNewOrg(true);
        getOrgPayload({ create_org: true });
      }
    }
  };

  //   useEffect(() => {
  //     addChunkInCRMBulkSearchData({
  //       recordId: org?.organization_id,
  //       data: org?.people?.[0]?.references?.[0]?.records ? org?.people?.[0]?.references?.[0]?.records : [],
  //     });
  //   }, [org]);

  useEffect(() => {
    if (
      org &&
      org.people &&
      org.people.length > 0 &&
      org.people[0].references &&
      org.people[0].references.length > 0 &&
      org.people[0].references[0]?.search_filters_applied
    ) {
      setSearchFilterData(org.people[0].references[0]?.search_filters_applied);

      processOrgSearchData(org.people[0].references);
    }

    if (!isCRMBulkSearchLoading && org && org.people && org.people.length > 0 && !org.people[0].references) {
      processOrgSearchData(getCRMSearchRecordById(org?.organization_id)?.records);
      let orgPayload = {
        create_org: false,
      };
      if (selectedCompanyId) {
        orgPayload.reference_fields = {
          [query?.crm_object_api_name_company]: selectedCompanyId,
        };
      }
      getOrgPayload(orgPayload);
    }
  }, [org]);

  useEffect(() => {
    if (selectedCompanyId) {
      let orgPayload = {
        create_org: false,
      };
      orgPayload.reference_fields = {
        [query?.crm_object_api_name_company]: selectedCompanyId,
      };
      getOrgPayload(orgPayload);
    }
  }, [selectedCompanyId]);

  // useEffect(() => {
  //   if (crmBulkCreateData && crmBulkCreateData.length > 0) {
  //     processOrgSearchData(getCRMSearchRecordById(org?.organization_id)?.records, selectedCompanyId);
  //     let orgPayload = {
  //       create_org: false,
  //     };
  //     if (selectedCompanyId) {
  //       orgPayload.reference_fields = {
  //         [query?.crm_object_api_name_company]: selectedCompanyId,
  //       };
  //     }
  //     getOrgPayload(orgPayload);
  //   }
  // }, [crmBulkCreateData, org]);

  const { isOpen, openModal, closeModal } = useModal();

  const [companyFormInputFields, setCompanyFormInputFields] = useState({});
  const [shouldCreateNewOrg, setShouldCreateNewOrg] = useState(false);

  const queryParams = useQueryParams();

  const removeContactFromLocalStorageByRecord = (recordId) => {
    if (!recordId) return;

    const payloadStr = localStorage.getItem(queryParams?.payload);
    console.log("payloadStr", payloadStr);
    if (!payloadStr) return;

    try {
      const payload = JSON.parse(payloadStr);

      if (Array.isArray(payload.contacts)) {
        payload.contacts = payload.contacts.filter(
          (contact) => contact.person_id !== recordId && contact.contact_id !== recordId
        );

        localStorage.setItem(queryParams?.payload, JSON.stringify(payload));
        console.log(`Removed record_id ${recordId} from ${queryParams?.payload}`);
      }
    } catch (err) {
      console.error("Failed to parse localStorage payload:", err);
    }
  };

  function hasMissingRequiredFields(fieldsArray, recordObj) {
    return fieldsArray.some((field) => {
      const { key, required } = field;

      if (!required || recordObj.record_exists) return false;

      const value = recordObj[key];

      return value === null || value === undefined || value === "";
    });
  }

  const getRecordType = (record) => {
    let recordType = "all_records";
    if (record.record_exists) {
      recordType = "in_crm";
    } else if (record.has_error) {
      recordType = "action_required";
    } else {
      recordType = "ready_to_add";
    }
    return recordType;
  };

  return (
    <div
      className={`mt-5 border border-gray-300 rounded-md records__row__container ${tableRowsWithWithoutIsRevealed && tableRowsWithWithoutIsRevealed.length === 0 && "hidden"} 
      ${allRecordsAlreadyPushed && "all__records__pushed"}
      `}
    >
      <div
        className={`p-4 rounded-t-md border-b border-gray-300 flex items-center justify-between w-full ${
          ((!matchedObj?.match_confirmed &&
            allRecordsWithApiName.length > 1 &&
            hasReferences &&
            !selectedCompanyId &&
            !shouldCreateNewOrg) ||
            (savedCompanyConfigWithWritePermission &&
              savedCompanyConfigWithWritePermission.length === 0 &&
              hasReferences &&
              allRecordsWithApiName &&
              allRecordsWithApiName.length === 0 &&
              !shouldCreateNewOrg)) &&
          !selectedCompanyId &&
          !allRecordsAlreadyPushed
            ? "bg-yellow-100"
            : shouldCreateNewOrg && !allRecordsAlreadyPushed
              ? "bg-brand-100"
              : "bg-gray-50"
        }`}
      >
        <span className="inline-flex flex-col">
          <div>{org?.name}</div>
          {shouldCreateNewOrg && !allRecordsAlreadyPushed && (
            <div>Creating new {query?.crm_object_label_company}</div>
          )}
        </span>
        {hasReferences && (
          <>
            <span className="inline-flex items-center justify-between space-x-2" target="_blank">
              <span className="inline-flex items-center space-x-2">
                {/* CRM Logo */}
                {!allRecordsAlreadyPushed && (
                  <Image
                    src={`/images/crm/${activeCRMIntegration?.integration?.key}-${selectedRecord?.match_confirmed || selectedCompanyId ? "blue" : "gray"}.svg`}
                    className="h-4"
                  />
                )}

                {/* Company Name */}
                <span className="text-[#101828] text-sm font-semibold">
                  {shouldCreateNewOrg
                    ? companyFormInputFields?.name || searchFilterData?.["name"]?.value
                    : selectedRecord?.Name ||
                      selectedRecord?.data?.name?.value ||
                      `${selectedRecord?.data?.first_name?.value || ""} ${selectedRecord?.data?.last_name?.value || ""}`}
                </span>

                {/* Matched Icon */}
                {!shouldCreateNewOrg && selectedRecord?.match_confirmed && (
                  <Image src="/images/ext/crm/matched.svg" />
                )}
              </span>

              {/* Record Link */}
              {!shouldCreateNewOrg && selectedRecord?.record_url && (
                <a href={selectedRecord?.record_url} target="_blank">
                  <Image src="/images/ext/crm/hyperlink.svg" />
                </a>
              )}
              {shouldCreateNewOrg &&
                getCRMCreateRecordById(org?.organization_id) &&
                getCRMCreateRecordById(org?.organization_id)?.record_url && (
                  <>
                    <Image src="/images/ext/crm/matched.svg" />
                    <a href={getCRMCreateRecordById(org?.organization_id)?.record_url} target="_blank">
                      <Image src="/images/ext/crm/hyperlink.svg" />
                    </a>
                  </>
                )}

              {/* ---- ACTION BUTTONS ---- */}

              {/* If selectedCompanyId exists → always show Edit */}
              {!allRecordsAlreadyPushed && (
                <>
                  {selectedCompanyId ? (
                    <Button variant="outline" className="h-9" onClick={() => {
                      openModal();
                      captureEvent("ECRM_BulkContacts_OpenAccountModal", {
                        organization_id: org?.organization_id,
                        organization_name: org?.name,
                        action: "edit",
                      });
                    }} id={org?.organization_id}>
                      <Pencil width={14} height={14} className="mr-2" /> Edit
                    </Button>
                  ) : shouldCreateNewOrg ? (
                    <Button variant="outline" className="h-9" onClick={() => {
                      openModal();
                      captureEvent("ECRM_BulkContacts_OpenAccountModal", {
                        organization_id: org?.organization_id,
                        organization_name: org?.name,
                        action: "edit_create_new",
                      });
                    }} id={org?.organization_id}>
                      <Pencil width={14} height={14} className="mr-2" /> Edit
                    </Button>
                  ) : allRecordsWithApiName.length === 1 ? (
                    <Button variant="outline" className="h-9" onClick={() => {
                      openModal();
                      captureEvent("ECRM_BulkContacts_OpenAccountModal", {
                        organization_id: org?.organization_id,
                        organization_name: org?.name,
                        action: "edit_single_match",
                      });
                    }} id={org?.organization_id}>
                      <Pencil width={14} height={14} className="mr-2" /> Edit
                    </Button>
                  ) : allRecordsWithApiName.length > 1 ? (
                    <>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="mr-3"
                      >
                        <path
                          d="M6.25033 9.16667H3.83366C3.36695 9.16667 3.13359 9.16667 2.95533 9.25749C2.79853 9.33739 2.67105 9.46487 2.59115 9.62167C2.50033 9.79993 2.50033 10.0333 2.50033 10.5V17.5M13.7503 9.16667H16.167C16.6337 9.16667 16.8671 9.16667 17.0453 9.25749C17.2021 9.33739 17.3296 9.46487 17.4095 9.62167C17.5003 9.79993 17.5003 10.0333 17.5003 10.5V17.5M13.7503 17.5V5.16667C13.7503 4.23325 13.7503 3.76654 13.5687 3.41002C13.4089 3.09641 13.1539 2.84144 12.8403 2.68166C12.4838 2.5 12.0171 2.5 11.0837 2.5H8.91699C7.98357 2.5 7.51686 2.5 7.16034 2.68166C6.84674 2.84144 6.59177 3.09641 6.43198 3.41002C6.25033 3.76654 6.25033 4.23325 6.25033 5.16667V17.5M18.3337 17.5H1.66699M9.16699 5.83333H10.8337M9.16699 9.16667H10.8337M9.16699 12.5H10.8337"
                          stroke="#DC6803"
                          strokeWidth="1.66667"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>Multiple accounts found</span>
                      <Button className="h-9" onClick={() => {
                        openModal();
                        captureEvent("ECRM_BulkContacts_OpenAccountModal", {
                          organization_id: org?.organization_id,
                          organization_name: org?.name,
                          action: "choose_multiple",
                          matches_count: allRecordsWithApiName.length,
                        });
                      }} id={org?.organization_id}>
                        <Pencil width={14} height={14} className="mr-2" /> Choose account
                      </Button>
                    </>
                  ) : savedCompanyConfigWithWritePermission?.length === 0 &&
                    hasReferences &&
                    allRecordsWithApiName?.length === 0 ? (
                    <>
                      <span className="text-[#101828] text-sm font-semibold">No account found</span>
                      <Button className="h-9" onClick={() => {
                        openModal();
                        captureEvent("ECRM_BulkContacts_OpenAccountModal", {
                          organization_id: org?.organization_id,
                          organization_name: org?.name,
                          action: "choose_no_match",
                        });
                      }} id={org?.organization_id}>
                        <Pencil width={14} height={14} className="mr-2" /> Choose account
                      </Button>
                    </>
                  ) : (
                    savedCompanyConfigWithWritePermission?.length > 0 &&
                    allRecordsWithApiName?.length === 0 && (
                      <Button variant="outline" className="h-9" onClick={() => {
                        openModal();
                        captureEvent("ECRM_BulkContacts_OpenAccountModal", {
                          organization_id: org?.organization_id,
                          organization_name: org?.name,
                          action: "edit_no_match",
                        });
                      }} id={org?.organization_id}>
                        <Pencil width={14} height={14} className="mr-2" /> Edit
                      </Button>
                    )
                  )}
                </>
              )}
            </span>
          </>
        )}
      </div>
      <div className=" overflow-x-auto">
        <table className="w-full border-collapse text-sm text-left">
          <thead className="bg-gray-100 text-gray-700 font-semibold">
            <tr>
              <th className="px-4 py-2 border-b border-gray-300 border-r border-gray-200 whitespace-pre sticky left-0 z-10 bg-gray-100 shadow-right ">
                <span className="w-[450px] inline-block">Name</span>
              </th>
              {tableHeads &&
                tableHeads.length > 0 &&
                tableHeads.map((th) => (
                  <th className="px-4 py-2 border-b border-gray-300 border-r border-gray-200 whitespace-pre">
                    {th?.label} {th?.required && <span className="text-red-500">*</span>}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {tableRowsWithWithoutIsRevealed &&
              tableRowsWithWithoutIsRevealed.length > 0 &&
              tableRowsWithWithoutIsRevealed.map((item, index) => (
                <tr
                  data-record-type={getRecordType({
                    record_exists: item.record_exists,
                    has_error: (item && item.has_error) || hasMissingRequiredFields(tableHeads, item),
                  })}
                  className={`record__validation__row ${item && item.has_error ? "bg-red-50 " : ""}`}
                  id={item?.record_id}
                >
                  <td
                    className={`px-4 py-2 border-b border-r   ${(item && item.has_error) || hasMissingRequiredFields(tableHeads, item) ? "border-red-200 bg-red-100" : "border-gray-200 bg-white"} sticky left-0 z-10  shadow-right`}
                  >
                    <div className="flex items-center whitespace-pre justify-between space-x-4 w-[450px]">
                      <div className="inline-flex items-center">
                        {!item?.isRevealed && (
                          <TooltipWrapper content="No contact details" className="w-[20px] h-[20px] mr-3">
                            <Image src="/images/icons/untitledui/alert-warning-outline.svg" className="" />
                          </TooltipWrapper>
                        )}

                        {((item && item.has_error) || hasMissingRequiredFields(tableHeads, item)) && (
                          <TooltipWrapper
                            content={
                              hasMissingRequiredFields(tableHeads, item)
                                ? "There is some issue with this record"
                                : item?.crm_response_info?.error?.description
                            }
                            className="w-[20px] min-w-[20px] h-[20px] mr-3"
                          >
                            <Image src={`/images/ext/crm/error.svg`} className="" />
                          </TooltipWrapper>
                        )}

                        <div className="h-9 w-9 flex-shrink-0 items-center bg-gray-100 border border-border justify-center inline-flex rounded-full text-xs">
                          {item?.first_name &&
                            item?.last_name &&
                            getInitials(`${item?.first_name} ${item?.last_name}`)}
                        </div>
                        <div className="ml-4 pr-4">
                          <div className="font-bold text-gray-800 inline-flex items-center">
                            <span>
                              <EllipsisText
                                charLimit={24}
                              >{`${item?.first_name} ${item?.last_name}`}</EllipsisText>
                            </span>{" "}
                            <Badge variant="gray" className="ml-2">
                              {query?.crm_object_api_name}
                            </Badge>
                            {item?.record_url && (
                              <a href={item?.record_url} target="_blank" className="w-6 h-6 inline-block">
                                <Image src="/images/ext/crm/hyperlink.svg" />
                              </a>
                            )}
                          </div>
                          <div className="mt-0 text-gray-500">
                            <EllipsisText>{item?.job_title}</EllipsisText>
                          </div>
                        </div>
                      </div>
                      {item.record_exists && (
                        <span className="w-14 inline-flex items-center justify-between">
                          <Image
                            src={`/images/crm/${activeCRMIntegration?.integration?.key}-blue.svg`}
                            className="h-4"
                          />
                          <Image src={`/images/ext/crm/matched.svg`} className="ml-2" />
                        </span>
                      )}
                      {tableRowsWithWithoutIsRevealed.length > 0 && !item.record_exists && !disableDelete && (
                        <Button
                          variant="danger-outline"
                          onClick={() => {
                            setTableRowsWithWithoutIsRevealed((prev) =>
                              prev.filter((el) => el.record_id !== item?.record_id)
                            );
                            removePersonFromPayload(item?.record_id);
                            removeContactFromLocalStorageByRecord(item?.record_id);
                            captureEvent("ECRM_BulkContacts_DeleteContact", {
                              record_id: item?.record_id,
                              contact_name: `${item?.first_name} ${item?.last_name}`,
                              organization_id: org?.organization_id,
                            });
                          }}
                        >
                          <Trash2Icon width={16} height={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                  {tableHeads &&
                    tableHeads.length > 0 &&
                    tableHeads.map((th) => (
                      <td
                        className={`px-4 py-2 border-b border-r  ${((item && item.has_error) || (!item[th.key] && th.required)) && !item.record_exists ? "border-red-200 bg-red-100" : "border-gray-200 bg-white"}`}
                        onClick={() => setEditingCell({ rowIndex: index, key: th.key })}
                      >
                        <div className="inline-flex items-center space-x-2">
                          <div>
                            {editingCell.rowIndex === index &&
                            editingCell.key === th.key &&
                            !item.record_exists ? (
                              <>
                                {customFieldsObj[th.key] &&
                                customFieldsObj[th.key].picklist_values &&
                                customFieldsObj[th.key].picklist_values.length > 0 ? (
                                  <select
                                    name={th.key}
                                    className="rounded-[8px] w-full bg-white border border-[#dddddd] text-[#11181C] text-[14px] p-2 h-10 min-w-[150px]"
                                    onChange={(e) => handleCellEdit(index, th.key, e.target.value)}
                                    onBlur={() => setEditingCell({ rowIndex: null, key: null })}
                                    value={item[th.key] ?? ""}
                                  >
                                    <option value="">--Select--</option>
                                    {customFieldsObj[th.key].picklist_values.map((el) => (
                                      <option key={el} value={el}>
                                        {el}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <Input
                                    className="min-w-[150px]"
                                    autoFocus
                                    value={item[th.key] ?? ""}
                                    onBlur={() => setEditingCell({ rowIndex: null, key: null })}
                                    onChange={(e) => handleCellEdit(index, th.key, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") setEditingCell({ rowIndex: null, key: null });
                                    }}
                                  />
                                )}
                              </>
                            ) : (
                              <TooltipWrapper content={!item.record_exists && "Click to edit"}>
                                <span className="inline-block border border-transparent whitespace-pre">
                                  {item[th?.key] || "-"}
                                </span>
                              </TooltipWrapper>
                            )}
                          </div>

                          {isUUIDKey(th.key) && item[th.key] && !item.record_exists && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="ml-2 inline-flex items-center space-x-1 focus:outline-none rounded border border-gray-300 py-1.5 px-2.5"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Copy width={16} height={16} />
                                  <CaretDownIcon width={16} height={16} />
                                </button>
                              </DropdownMenuTrigger>

                              <DropdownMenuContent className="w-full mt-1.5 ml-3 z-50" align="end">
                                <DropdownMenuGroup>
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingCell({ rowIndex: null, key: null });
                                      copyCellValueToRows(index, th.key, "empty");
                                    }}
                                  >
                                    <Copy width={16} height={16} className="mr-2" />
                                    Copy to empty rows
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingCell({ rowIndex: null, key: null });
                                      copyCellValueToRows(index, th.key, "all");
                                    }}
                                  >
                                    <Copy width={16} height={16} className="mr-2" />
                                    Copy to all rows
                                  </DropdownMenuItem>
                                </DropdownMenuGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </td>
                    ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <GenericModal isOpen={isOpen} closeModal={closeModal} width="w-[1000px]">
        <CompanyRecords
          initCompanyId={selectedCompanyId}
          initialPayloadforForm={companyFormInputFields}
          getGeneratedPayload={(data) => {
            setCompanyFormInputFields(data ? data : {});

            let obj = {
              organization_id: org?.organization_id,
              crm_object_api_name: query?.crm_object_api_name_company,
              type: "company",
              create_org: true,
            };
            if (data) {
              obj.input_fields = data;
            }
            getOrgPayload(obj);
            setShouldCreateNewOrg(true);
            setSelectedCompanyId("");
          }}
          openCreateRecord={
            ((allRecordsWithApiName && allRecordsWithApiName.length === 0) || shouldCreateNewOrg) &&
            savedCompanyConfigWithWritePermission &&
            savedCompanyConfigWithWritePermission.length > 0
              ? true
              : false
          }
          // openCreateRecord={true}
          searchFilter={searchFilterData}
          // companyId={query?.company}
          companyId={org?.organization_id}
          hideCompanyRecords={closeModal}
          submitFromCompanyRecord={(companyId, data) => {
            setSelectedCompanyId(companyId);
            if (companyId) {
              getOrgPayload({
                create_org: false,
                reference_fields: {
                  ...data,
                },
              });
            } else {
              getOrgPayload({
                create_org: false,
              });
            }

            setShouldCreateNewOrg(false);
            closeModal();
            const crmRecords = getCRMSearchRecordByIdFlat(org?.organization_id);
            const findSelectedObj =
              crmRecords && crmRecords.length > 0 && crmRecords.find((el) => el?.Id === companyId);
            setSelectedRecord(findSelectedObj);
          }}
          activeCRMIntegration={activeCRMIntegration}
        />
      </GenericModal>
    </div>
  );
}
